import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { times } from 'lodash'
import { Helmet } from 'react-helmet'
import { Padding } from 'web-makeup/dist/design-system/styles/spacings'
import Skeleton from '@yisheng90/react-loading'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'

import BrandWalletCard from '../components/BrandWalletCard/BrandWalletCard'
import NewBrandDashboard from '../BrandDashboard'
import NoOrders from '../components/NoOrders/NoOrders'
import {
  BreadCrumbs,
  NewBriefBanner,
} from '../BrandDashboardTemplates/BrandDashboardTemplates.style'
import DefaultButton from '../components/DefaultButton/DefaultButton'

import { client as apolloClient } from '../../ApolloProvider/ApolloProvider'
import { ProjectLevel, ProjectState } from '../../../typings/types'
import { GET_ALL_PROJECTS } from '../../../queries/GetAllProjectsv2'
import { filterByState } from '../../../helpers/filterProjects'
import { validateProjects } from '../../../helpers/allProjectsPaginatedDataHelper'
import PurchaseRedesigned from '../../../components/PurchaseRedesigned/PurchaseRedesigned'

import {
  OrdersHeader,
  SectonTitle,
  BrandWalletCards,
  LoadingCardsWrapper,
  NavbarStyles,
  StyledCardNew,
} from './BrandDashboardWallets.style'

interface BrandDashboardWalletsProps {
  match?: { params: { id: string } }
  searchToken?: string
}

const BrandDashboardWallets: React.FC<BrandDashboardWalletsProps> = ({ match, searchToken }) => {
  React.useEffect(() => {
    document.body.className = ''
    document.body.classList.add('wallets')
    return () => {
      document.body.className = ''
      document.body.classList.add('wallets')
    }
  }, [])

  const brandId = match && match.params.id

  const { data: walletProjectData, loading: walletProjectDataLoading } = useQuery(
    GET_ALL_PROJECTS,
    {
      variables: {
        brandUuid: brandId,
        pageSize: 1000,
        projectLevels: [ProjectLevel.PRE_PAY_PACKAGE],
        orderDirection: 'DESC',
      },
      client: apolloClient,
      errorPolicy: 'all',
    }
  )

  const activeWallets = filterByState(validateProjects(walletProjectData), ProjectState.ACTIVE)
  const completedWallets = filterByState(
    validateProjects(walletProjectData),
    ProjectState.COMPLETED
  )
  const purchasingWallets = filterByState(
    validateProjects(walletProjectData),
    ProjectState.PURCHASING
  )

  const ProjectCardSkeletonNew = (
    <StyledCardNew data-testid="project_card__skeleton">
      <Padding top bottom left right size="mSmall">
        <Skeleton width="100%" height={164} />
        <Padding bottom size="small" />
        <Skeleton width="60%" height={36} />
        <Padding bottom size="small" />
        <Skeleton width="50%" height={36} />
      </Padding>
    </StyledCardNew>
  )
  const PageLoadingTabs = () => {
    return (
      <LoadingCardsWrapper>
        {times(12, () => {
          return <div>{ProjectCardSkeletonNew}</div>
        })}
      </LoadingCardsWrapper>
    )
  }

  interface BrandOrdersProps {
    brandOrderUuid?: string
  }
  const BrandOrders: React.FC<BrandOrdersProps> = ({ brandOrderUuid }) => {
    const [isCreatePageVisible, setIsCreatePageVisible] = React.useState(false)
    const breadcrumbIcon = (
      <svg width="8" height="16" viewBox="0 0 8 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.146447 0.646447C0.341709 0.451184 0.658291 0.451184 0.853553 0.646447L7.85355 7.64645C7.94732 7.74021 8 7.86739 8 8C8 8.13261 7.94732 8.25979 7.85355 8.35355L0.853553 15.3536C0.658291 15.5488 0.341709 15.5488 0.146447 15.3536C-0.0488155 15.1583 -0.0488155 14.8417 0.146447 14.6464L6.79289 8L0.146447 1.35355C-0.0488155 1.15829 -0.0488155 0.841709 0.146447 0.646447Z"
          fill="#9E9E9E"
        />
      </svg>
    )
    return (
      <>
        <NavbarStyles />
        {isCreatePageVisible ? (
          <>
            <BreadCrumbs isCreateView={isCreatePageVisible}>
              <span onClick={() => setIsCreatePageVisible(false)}>Wallet</span>
              {breadcrumbIcon}
              <span onClick={() => setIsCreatePageVisible(true)}>Submit a video brief</span>
            </BreadCrumbs>
            <PurchaseRedesigned />
          </>
        ) : (
          <>
            <OrdersHeader>
              <h3 data-testid="header-title">Wallets</h3>
            </OrdersHeader>
            <NewBriefBanner>
              <div>
                <h3>Create a new wallet and get bonus credits</h3>
                <p>
                  Create a new wallet on 90 Seconds to simplify your budgeting and get bonus credits
                  up to 25%.
                </p>
              </div>
              <DefaultButton
                fontSize="1rem"
                padding="0 1.5rem"
                backgroundColor={colors.white}
                color="#1936D0"
                buttonHeight="3.375rem"
                rounded
                hoverBackground={colors.white}
                hoverColor="#1936D0"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => setIsCreatePageVisible(!isCreatePageVisible)}
              >
                Create a new wallet
              </DefaultButton>
            </NewBriefBanner>

            {walletProjectDataLoading && PageLoadingTabs()}
            {!walletProjectDataLoading &&
              activeWallets &&
              completedWallets &&
              purchasingWallets &&
              activeWallets.length === 0 &&
              completedWallets.length === 0 &&
              purchasingWallets.length === 0 && (
                <NoOrders
                  icon="v1640694520/-design-system/-icons-2022/-illustrated/icon-bg-wallet_2x.png"
                  title="You don’t have any wallets."
                  content="Contact with your brand manager to learn more about wallets."
                  buttonText="Talk with us"
                  buttonOnClick={() =>
                    window &&
                    window.HubSpotConversations &&
                    window.HubSpotConversations.widget.open()
                  }
                />
              )}
            {purchasingWallets &&
              purchasingWallets.length > 0 && (
                <>
                  <SectonTitle>In purchase</SectonTitle>
                  <BrandWalletCards>
                    {purchasingWallets.map(project => {
                      return <BrandWalletCard project={project} key={project.uuid} />
                    })}
                  </BrandWalletCards>
                </>
              )}
            {activeWallets &&
              activeWallets.length > 0 && (
                <>
                  <SectonTitle>Active</SectonTitle>
                  <BrandWalletCards>
                    {activeWallets.map(project => {
                      return <BrandWalletCard project={project} key={project.uuid} />
                    })}
                  </BrandWalletCards>
                </>
              )}
            {completedWallets &&
              completedWallets.length > 0 && (
                <>
                  <SectonTitle>Completed</SectonTitle>
                  <BrandWalletCards>
                    {completedWallets.map(project => {
                      return <BrandWalletCard project={project} key={project.uuid} completed />
                    })}
                  </BrandWalletCards>
                </>
              )}
          </>
        )}
      </>
    )
  }
  const flags = useFlags()
  const enable2024NavigationBlack = flags['2024-navigation-black']
  const activeButton = enable2024NavigationBlack ? 3 : 2

  return (
    <>
      <Helmet>
        <title>Dashboard - 90 Seconds</title>
      </Helmet>
      <NewBrandDashboard
        brandId={brandId}
        activeButton={activeButton}
        children={<BrandOrders />}
        subHeaderTitle="Wallets"
        searchToken={searchToken}
      />
    </>
  )
}

export default React.memo(BrandDashboardWallets)



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/BrandDashboardWallets/BrandDashboardWallets.tsx